import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { tagHotjarRecording, getCategoryCards } from '../../helpers/util'
import useRequest from '../../hooks/useRequest'
import useAuth from '../../hooks/useAuth'
import ContentLayout from '../../components/ContentLayout/ContentLayout'
import MainHeading from '../../components/MainHeading/MainHeading'
import Button from '../../components/Button/Button'
import { CheckIcon } from '@heroicons/react/outline'
import CardInfo from '../../components/CardInfo/CardInfo'
import EquipmentRequestForm from '../NewEquipmentRequestPage/components/EquipmentRequestForm'
import useGlobalLoading from '../../hooks/useGlobalLoading'
import useAlert from '../../hooks/useAlert'
import { isNil } from 'lodash'
import AppRoutes from '../../routes/appRoutes'
import useUsedEquipment from '../../hooks/useUsedEquipment'
import useScreen from '../../hooks/useScreen'
import clsx from 'clsx'

const cards = getCategoryCards()

const UsedEquipmentRequestFormPage = props => {
    const { history } = props
    const { user } = useAuth()
    const [error, setError] = useState(null)
    const [isValidForm, setIsValidForm] = useState(false)
    const {
        handleCreateRequestUsedEquipment,
        changeNeedsUpdate,
        needsUpdateDashboard,
    } = useRequest()
    const { changeLoading } = useGlobalLoading()
    const { handleChangeAlert, handleChangeOpen } = useAlert()
    const formRef = useRef()
    const { filters } = useUsedEquipment()

    const parsedFilters = {
        ...filters,
        selectedCategory:
            filters.selectedCategory !== 'allCategories'
                ? filters.selectedCategory
                : '',
    }

    useEffect(() => {
        if (error != null || !isNil(error)) {
            handleChangeAlert({
                title: 'Error',
                message: error,
                type: 'error',
            })
        }
    }, [error])

    const { isMobile } = useScreen()

    const onError = response => {
        changeLoading(false)
        const message = response?.data
            ? response?.data?.message
            : 'Unable to create used equipment request'
        setError(message)
        tagHotjarRecording('API Error')
    }

    const onCreatedRequestSuccess = () => {
        changeLoading(false)
        changeNeedsUpdate({
            dashboard: needsUpdateDashboard,
            request: true,
        })
        handleChangeAlert({
            type: 'success',
            title: 'Thank you for your interest!',
            message:
                'A Representative will be in touch shortly to talk about the specifics of your request.',
            textCancel: 'Return to the dashboard',
            textOk: 'New Search',
            onDone: () => {
                handleChangeOpen(false)
                history?.push(AppRoutes.equipmentFinder())
            },
            onCancel: () => {
                history?.push(AppRoutes.dashboard())
                handleChangeOpen(false)
            },
        })
    }

    const onSubmit = values => {
        const minCapacity =
            'minCapacity' in values ? values.minCapacity : minCapacity
        const maxCapacity =
            'maxCapacity' in values ? values.maxCapacity : maxCapacity
        if (
            parseInt(values?.minCapacity) > parseInt(values?.maxCapacity) ||
            parseInt(minCapacity) > parseInt(maxCapacity)
        ) {
            setError('Min. Capacity must be lower than the Max. Capacity')
            return
        }

        const isUsed = true
        const comments = 'comments' in values ? values.comments : ''
        const tireGroup = 'tireGroup' in values ? values.tireGroup : ''
        const forkLength = 'forkLength' in values ? values.forkLength : 0

        const userId = user?.id
        const companyId = user?.company?.id
        const fuelType = values?.fuelType

        const card = cards.find(card => card?.category == values?.categories)

        const params = {
            userId,
            companyId,
            values,
            minCapacity,
            maxCapacity,
            comments,
            card,
            fuelType,
            forkLength,
            tireGroup,
            isUsed,
        }

        changeLoading(true)
        handleCreateRequestUsedEquipment(
            params,
            onCreatedRequestSuccess,
            onError,
        )
    }
    const renderButtons = () => {
        return (
            <div className="flex-grow lg:w-auto w-full right-0 left-0 h-auto flex items-center justify-center gap-x-2">
                <Button
                    key="cancelCreationRequest"
                    text="Cancel"
                    onClick={() => {
                        history?.goBack()
                    }}
                    variant="tertiary"
                    className="lg:w-auto w-full flex-grow"
                />
                <Button
                    key="createRequest"
                    text="Submit Request"
                    disabled={!isValidForm}
                    className="lg:w-auto w-full flex-grow"
                    onClick={() => {
                        if (formRef?.current) {
                            formRef?.current?.handleSubmit()
                        }
                    }}
                    variant="primary"
                    type="submit"
                    icon={
                        <CheckIcon className="text-yellow stroke-current stroke-2 w-5 h-5 mr-2" />
                    }
                />
            </div>
        )
    }

    return (
        <ContentLayout>
            <MainHeading
                isTabletViewPort
                title="Used Equipment Finder"
                subtitle="Extend the size range of your current fleet of equipment"
                actions={(!isMobile && [renderButtons()]) || null}
            />

            <div
                className={clsx(
                    'w-full h-full overflow-auto flex flex-col items-center justify-start max-w-full max-h-full lg:px-6 lg:py-12 px-4 py-4',
                    isMobile && 'mb-24',
                )}
            >
                <div className="lg:w-220 w-full h-auto ">
                    <CardInfo
                        title="Request Details"
                        subTitle="Please tell us what you need"
                        isOverflowVisible={true}
                        content={
                            <EquipmentRequestForm
                                setError={setError}
                                onSubmit={onSubmit}
                                formRef={formRef}
                                initialValues={parsedFilters}
                                setIsValidForm={setIsValidForm}
                            />
                        }
                    />
                </div>
            </div>
            {isMobile && (
                <div className="w-full z-100 bg-white p-4 border-t border-grayLines h-auto absolute bottom-0 right-0">
                    {renderButtons()}
                </div>
            )}
        </ContentLayout>
    )
}

UsedEquipmentRequestFormPage.propTypes = {
    location: PropTypes.shape({
        search: PropTypes.string,
        state: PropTypes.shape({
            source: PropTypes.string,
        }),
    }),
    history: PropTypes.shape({
        goBack: PropTypes.func,
        push: PropTypes.func,
    }),
}

export default UsedEquipmentRequestFormPage
